/**
 * Keeps a `--header-height` CSS variable in sync with the real fixed-header
 * height so anchored sections can offset their scroll position beneath it
 * (via `scroll-margin-top`). Runs on load and on resize.
 *
 * @param {number} breathingRoom - Extra space below the header in pixels (default: 24px)
 * @returns {() => void} A cleanup function that removes the resize listener.
 */
export const initHeaderHeightVar = (breathingRoom = 24) => {
  const header = document.querySelector('header');
  if (!header) return () => {};

  const setHeaderHeightVar = () => {
    const height = header.offsetHeight + breathingRoom;
    document.documentElement.style.setProperty('--header-height', `${height}px`);
  };

  setHeaderHeightVar();
  window.addEventListener('resize', setHeaderHeightVar);

  return () => window.removeEventListener('resize', setHeaderHeightVar);
};

/**
 * Applies a `scroll-padding-top` offset on mobile so same-page anchor links land
 * below the fixed header. This complements the `scroll-margin-top` rule on
 * `section[id]` in CSS; closing the mobile menu on link clicks is handled in
 * mobileMenu.js, and the actual scroll is left to native hash navigation.
 *
 * @param {number} headerHeight - The height of the header in pixels (default: 88px)
 */
export const handleMobileAnchorLinks = (headerHeight = 88) => {
  const style = document.createElement('style');
  style.textContent = `
    html {
      scroll-padding-top: ${headerHeight}px;
    }
  `;
  document.head.appendChild(style);
};

/**
 * Handles links with a data-scroll-offset attribute for smooth scrolling with
 * an offset that clears the fixed header and any sticky sub-nav.
 *
 * Links inside a .sticky-sub-nav are deliberately excluded: sticky-sub-nav.js
 * binds those itself with an offset measured against its own bar, and binding
 * them here too would mean two handlers racing on the same click.
 */
export const handleScrollOffsetLinks = () => {
  // `:not()` with a descendant selector isn't supported everywhere, so filter
  // in JS instead of relying on it in the selector.
  const offsetLinks = [...document.querySelectorAll('a[data-scroll-offset]')]
    .filter(link => !link.closest('.sticky-sub-nav'));

  // Measured rather than hardcoded so it stays correct at every breakpoint.
  const getOffset = () => {
    const headerH = document.querySelector('header')?.offsetHeight ?? 0;
    const stickyNavH = document.querySelector('.sticky-sub-nav')?.offsetHeight ?? 0;
    return headerH + stickyNavH + 50;
  };

  offsetLinks.forEach(link => {
    link.addEventListener('click', (e) => {
      const href = link.getAttribute('href');
      if (!href || !href.startsWith('#') || href === '#') return;

      const targetId = href.substring(1);
      const targetElement = document.querySelector(`#${CSS.escape(targetId)}`);

      if (!targetElement) {
        console.warn(`[scroll-offset] No element with id="${targetId}" found on this page.`);
        return; // let the browser handle it (will do nothing if no target, but doesn't break)
      }

      e.preventDefault();
      scrollToOffset(targetElement, getOffset());
    });
  });

  // Cross-page links arrive with the hash already in the URL. Pages that have a
  // sticky sub-nav let sticky-sub-nav.js handle this so the two don't fight.
  if (window.location.hash && !document.querySelector('.sticky-sub-nav')) {
    const targetId = window.location.hash.substring(1);
    const targetElement = document.querySelector(`#${CSS.escape(targetId)}`);
    if (targetElement) {
      setTimeout(() => scrollToOffset(targetElement, getOffset()), 100);
    }
  }
};

/**
 * Helper function to scroll to an element with offset
 */
const scrollToOffset = (targetElement, offset) => {
  if (!targetElement) return;
  const targetPosition = targetElement.getBoundingClientRect().top + window.pageYOffset - offset;
  window.scrollTo({
    top: targetPosition,
    behavior: 'smooth'
  });
};
